<template>
  <div
    class="filter-search-filters filter-bar__wrapper"
    v-bind="$attrs"
  >
    <div class="filter-bar">
      <div class="filter-bar__left">
        <button
          class="filter-bar__trigger"
          type="button"
          @click="handleFilterDrawerToggle"
        >
          <span
            class="
              filter-bar__trigger-label
              filter-bar__trigger-label--mobile
              text-body-xs-regular
            "
            v-text="$string('filters.filter_and_sort_options')"
          />

          <span
            class="
              filter-bar__trigger-label
              filter-bar__trigger-label--desktop
              text-body-s-regular
            "
            v-text="$string('filters.filter_by')"
          />

          <icon-filter />

          <p
            class="filter-bar__product-count-mobile text-body-s-regular"
            v-text="$string('filters.total_product_count', {
              replace: { count: productCount },
              pluralise: productCount,
            })"
          />
        </button>
      </div>

      <p
        class="filter-bar__product-count-desktop text-body-s-regular"
        v-text="$string('filters.total_product_count', {
          replace: { count: productCount },
          pluralise: productCount,
        })"
      />

      <div class="filter-bar__right">
        <div class="filter-bar__column-toggle-container">
          <p
            class="filter-bar__grid-view text-body-s-regular"
            v-text="$string('filters.grid_view')"
          />

          <button
            type="button"
            class="filter-bar__column-toggle"
            :class="{ [$class('active')]: mobileColumnCount === 1 }"
            @click="handleGridColumnChange(1)"
          >
            <span
              class="visually-hidden"
              v-text="$string('filters.columns.1_per_row')"
            />
            <icon-grid-one />
          </button>

          <button
            type="button"
            class="filter-bar__column-toggle"
            :class="{ [$class('active')]: mobileColumnCount === 2 }"
            @click="handleGridColumnChange(2)"
          >
            <span
              class="visually-hidden"
              v-text="$string('filters.columns.2_per_row')"
            />
            <icon-grid-two />
          </button>
        </div>

        <div class="filter-bar__sort-desktop">
          <div class="filter-bar__sorting">
            <label
              class="filter-bar__sorting-label text-body-s-regular"
              for="filter-bar-sorting"
              v-text="`${$string('filters.sort_by')}:`"
            />
            <select
              id="filter-bar-sorting"
              v-model="localSorting.current"
              class="filter-bar__sorting-select text-body-s-regular"
              :style="{
                width: sortSelectWidth,
              }"
            >
              <option
                v-for="sortOption of localSorting.options"
                :key="sortOption.value"
                :value="sortOption.value"
                v-text="sortOption.name"
              />
            </select>
            <select
              ref="sortSelectResize"
              class="
                  filter-bar__sorting-select
                  filter-bar__sorting-select--resize
                  text-body-s-regular
                "
              disabled
              aria-hidden="true"
            >
              <option
                v-text="currentSortText"
              />
            </select>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="modifiedActiveFilters.length"
      class="filter-bar__filters"
    >
      <button
        class="filter-bar__clear-all"
        type="button"
        @click="handleClearAll"
      >
        <span
          class="text-body-s-underline"
          v-text="$string('filters.clear_all')"
        />
      </button>

      <div class="filter-bar__active-filters-container">
        <div class="filter-bar__active-filters">
          <button
            v-for="activeFilter of modifiedActiveFilters"
            :key="`${activeFilter.paramName}-${activeFilter.value}`"
            class="filter-bar__active-filter active-filter"
            type="button"
            @click="handleFilterChange(activeFilter)"
          >
            <icon-close />
            <span
              class="active-filter__label text-body-s-regular"
              v-text="getActiveFilterLabel(activeFilter)"
            />
          </button>
        </div>
      </div>
    </div>
  </div>

  <overlay
    key="filterDrawer"
    block-class="filter-search-drawer-overlay"
    direction="right"
    namespace="filterDrawer"
    :template="false"
    type="drawer"
  >
    <template #body="overlay">
      <filter-search-filter-drawer
        v-if="getComponentVisible('filter-search-filter-drawer')"
        :active-filters="modifiedActiveFilters"
        :filters="filters"
        :overlay="overlay"
        :initial-open-filter="initialOpenFilter"
        :product-count="productCount"
        :sorting="sorting"
        @filter-change="handleFilterChange"
        @filter-clear-all="handleClearAll"
        @filter-price-change="(payload) => $emit('filter-price-change', payload)"
        @sort-change="handleSortChange"
      />
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Filter search filters (filter-search-filters)
 * -----------------------------------------------------------------------------
 * Filtering components for filter and search.
 *
 * @param {Array} activeFilters - Array of active filter data.
 * @param {Array} filters - Array of all filter data.
 * @param {Number} productCount - Total product count.
 * @param {Array} sorting - Formatted sorting data.
 *
 */

// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions } from 'vuex'

import Overlay from '~global/overlay/overlay'
import IconFilter from '~icons/general/filter.svg'
import IconGridOne from '~icons/general/grid-one.svg'
import IconGridTwo from '~icons/general/grid-two.svg'
import IconClose from '~icons/directional-navigation/close.svg'

export default {
  name: 'FilterSearchFilters',

  components: {
    FilterSearchFilterDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.filter-search-filter-drawer' */
        './filter-search-filter-drawer'
      ),
    }),
    IconFilter,
    IconGridOne,
    IconGridTwo,
    IconClose,
    Overlay,
  },

  inheritAttrs: false,

  props: {
    activeFilters: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Array,
      default: () => [],
    },
    mobileColumnCount: {
      type: Number,
      default: 2,
    },
    productCount: {
      type: Number,
      default: 0,
    },
    sorting: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: {
    'filter-change': (payload) => typeof payload === 'object',
    'filter-clear-all': () => true,
    'filter-price-change': (payload) => typeof payload === 'object',
    'mobile-column-count-change': (payload) => typeof payload === 'number',
    'sort-change': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      localSorting: this.sorting,
      initialOpenFilter: '',
      sortSelectWidth: '',
    }
  },

  computed: {

    /**
     * Compute current sort label.
     * - Used to resize select element.
     * @returns {String}
     */
    currentSortText() {
      const currentSorting = this.localSorting.current || this.localSorting.default
      const activeSortingOption = this.localSorting.options.find((sortingOption) => {
        return sortingOption.value === currentSorting
      })

      return activeSortingOption.name
    },

    /**
     * Compute current sort label.
     * - Used to resize select element.
     * @returns {String}
     */
    modifiedActiveFilters() {
      const sizeFilterKeys = ['egg_size', 'color', 'texture', 'intensity']

      return this.activeFilters.map(filter => {
        const filterKey = filter.paramName.split('.').pop()
        const filterLabel = `${filterKey.replaceAll('_', ' ')}: ${filter.label}`

        return {
          ...filter,
          label: sizeFilterKeys.includes(filterKey) ? filterLabel : filter.label,
        }
      })
    },
  },

  watch: {

    /**
     * Watch the local sorting `current` value.
     * - Value changes due to v-model on the select element.
     * - Emits the new selected sorting value up to the parent component.
     */
    'localSorting.current'() {
      this.$emit('sort-change', this.localSorting.current)
    },

    currentSortText: {
      async handler() {
        await this.$nextTick()
        this.updateSelectWidth()
      },
      immediate: true,
    },
  },

  methods: {

    ...mapActions({
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Toggle the filter drawer.
     */
    handleFilterDrawerToggle() {
      this.toggleOverlay({
        component: 'filter-search-filter-drawer',
        ignoreDismissed: true,
        namespace: 'filterDrawer',
      })
    },

    /**
     * Handle filter change.
     * - Emits filter value object up to parent component.
     * @param {Object} filterValue - filter value object.
     */
    handleFilterChange(filterValue) {
      this.$emit('filter-change', filterValue)
    },

    /**
     * Handle sort change event.
     * - Updates local sorting state, which in turn emits an event using a
     *   watcher.
     * @param {String} sortValue - new sorting value
     */
    handleSortChange(sortValue) {
      this.localSorting.current = sortValue
    },

    /**
     * Handle Clear all button click.
     */
    handleClearAll() {
      this.$emit('filter-clear-all')
    },

    /**
     * Handle updating the mobile column count.
     * @param {Number} columnCount - Number of columns on mobile
     */
    handleGridColumnChange(columnCount) {
      this.$emit('mobile-column-count-change', columnCount)
    },

    /**
     * Open the filter drawer with a specific filter accordion active.
     * @param {String} filterName - Name of the filter to display opened.
     */
    handleFilterShortcut(filterName) {
      this.initialOpenFilter = filterName

      this.openOverlay({
        callback: this.handleFilterOverlayClose,
        component: 'filter-search-filter-drawer',
        ignoreDismissed: true,
        namespace: 'filterDrawer',
      })
    },

    /**
     * Handle filter drawer close.
     * - Resets the
     */
    handleFilterOverlayClose() {
      this.initialOpenFilter = ''
    },

    /**
     * Update sort select width on change.
     * - Read the width of the resize select and save it to data.
     * - Width is then applied to the visible select with a Vue style binding.
     */
    updateSelectWidth() {
      const mainSelectResize = this.$refs.sortSelectResize

      if (!mainSelectResize) {
        return
      }

      this.sortSelectWidth = `${mainSelectResize.clientWidth}px`
    },

    /**
     * Generate the label for the active filter button.
     * - Handles special formatting for price range.
     */
    getActiveFilterLabel(filterValue) {
      if (filterValue.type === 'price_range') {
        const min = filterValue.minValue.value || 0
        const max = filterValue.maxValue.value || filterValue.rangeMax

        return `${this.$formatMoney(min)} - ${this.$formatMoney(max)}`
      }

      return filterValue.label
    },
  },
}
</script>

<style lang="scss">
@import './filter-search-filters';
</style>
